import { Button } from "@admin/components/atoms/Button";
import { Input } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { dateToISO, Shift, updateShift } from "@enos-monorepo/lib";
import { useMutation,useQueryClient } from "@tanstack/react-query";
import { useEffect,useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface EditPlannedShiftModalProps extends ModalProps {
  shift?: Shift;
  companyLocationId: string;
  companyId: string;
}

export function EditPlannedShiftModal({
  shift,
  companyLocationId,
  companyId,
  ...props
}: EditPlannedShiftModalProps) {
  const { t } = useTranslation();
  const [calculatedTime, setCalculatedTime] = useState(0);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const onSubmit = () => {
    const startDate: Date = new Date(shift?.plannedStartDate as string);
    const endDate: Date = new Date(shift?.plannedEndDate as string);
    if (startTime) {
      const [startHours, startMinutes] = startTime.split(":").map(Number);
      startDate.setHours(startHours, startMinutes, 0, 0);
    }
    if (endTime) {
      const [endHours, endMinutes] = endTime.split(":").map(Number);
      endDate.setHours(endHours, endMinutes, 0, 0);
    }
    const payload = {
      id: shift?.id ?? "",
      companyId: companyId,
      companyLocationId: companyLocationId,
      plannedStartDate: dateToISO(startDate),
      plannedEndDate: dateToISO(endDate),
      userId: shift?.userId ?? "",
    };
    mutation.mutate(payload);
  };

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: updateShift,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["shifts", companyLocationId],
      });
      props.onClose?.();
      toast.success(t("shift.The shift has been edited"), {
        duration: 4000,
      });
    },
    onError: (err: Error) => {
      toast.error(t("shift.Unable to edit the shift"));
    },
  });

  const calculateTimeDifference = (start: string, end: string): number => {
    if (!start || !end) return 0.0;

    const [startHour, startMinute] = start.split(":").map(Number);
    const [endHour, endMinute] = end.split(":").map(Number);

    const startDate = new Date();
    startDate.setHours(startHour, startMinute, 0);

    const endDate = new Date();
    endDate.setHours(endHour, endMinute, 0);

    if (endDate < startDate) {
      endDate.setDate(endDate.getDate() + 1);
    }
    const diffInMs = endDate.getTime() - startDate.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    return parseFloat(diffInHours.toFixed(2));
  };

  useEffect(() => {
    const timeDiff = calculateTimeDifference(startTime ?? "", endTime ?? "");
    setCalculatedTime(timeDiff);
  }, [startTime, endTime]);

  useEffect(() => {
    setStartTime(shift?.plannedStartDate?.split("T")[1] ?? "");
    setEndTime(shift?.plannedEndDate?.split("T")[1] ?? "");
    const timeDiff = calculateTimeDifference(
      shift?.plannedStartDate?.split("T")[1] ?? "",
      shift?.plannedEndDate?.split("T")[1] ?? "",
    );
    setCalculatedTime(timeDiff);
  }, [shift]);

  return (
    <Modal
      actionButtons={
        <>
          <Button variant="secondary" onClick={props?.onClose}>
            {t("common.Discard")}
          </Button>
          <Button loading={mutation.isPending} onClick={onSubmit}>
            {t("shift.Edit Shift")}
          </Button>
        </>
      }
      {...props}
      title={t("shift.Edit Shift for Today", {
        name: `${shift?.firstName} ${shift?.lastName}`,
      })}
    >
      <form>
        <Label title={t("shift.Shift Beginning and End")}>
          <div className="flex flex-row justify-between w-full gap-2">
            <div className="flex flex-col w-full gap-y-1">
              <Input
                value={
                  startTime || shift?.plannedStartDate?.split("T")[1] || ""
                }
                onChange={(e) => setStartTime(e.target.value)}
                type="time"
              />
            </div>
            <div className="flex flex-col w-full gap-y-1">
              <Input
                value={endTime || shift?.plannedEndDate?.split("T")[1] || ""}
                onChange={(e) => setEndTime(e.target.value)}
                type="time"
              />
            </div>
          </div>
        </Label>

        <Label title={t("shift.Calculated Time")}>
          <Input value={calculatedTime} disabled />
        </Label>
      </form>
    </Modal>
  );
}
